import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from 'lucide'
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = [
    "beatSection",
    "noteInput",
    "messagesContainer",
    "input",
    "sendButton",
    "feedbackButton",
    "currentBeatName",
    "voiceToggle",
    "voiceOnIcon",
    "voiceOffIcon",
    "todosContainer",
    "remainingBadge",
    "remainingCount",
    "chatForm",
    "upgradePrompt"
  ]

  static values = {
    feedbackUrl: String,
    chatUrl: String,
    speakUrl: String,
    updateTodosUrl: String,
    updateHistoryUrl: String,
    remaining: Number,
    limit: Number,
    upgradeUrl: String
  }

  connect() {
    this.conversationHistories = {} // Per-beat conversation history
    this.beatTodos = {} // Per-beat todos: { beatId: [{ text: "...", completed: false }, ...] }
    this.isLoading = false
    this.currentBeatId = null
    this.voiceEnabled = true // Default to enabled
    this.currentAudio = null

    // Load initial todos and history from data attributes
    this.loadInitialTodos()
    this.loadInitialHistory()

    // Check if already at limit
    this.checkLimitReached()

    // Default to the first beat
    this.selectFirstBeat()
  }

  checkLimitReached() {
    // null/undefined remaining means unlimited
    if (this.remainingValue === null || this.remainingValue === undefined) return

    if (this.remainingValue <= 0) {
      this.showUpgradePrompt()
    }
  }

  updateRemainingCount(remaining) {
    // null means unlimited
    if (remaining === null || remaining === undefined) return

    this.remainingValue = remaining

    // Update all remaining count displays
    this.remainingCountTargets.forEach(el => {
      el.textContent = remaining
    })

    // Update badge styling based on remaining
    this.remainingBadgeTargets.forEach(badge => {
      badge.classList.remove("text-gray-500", "text-amber-600", "text-red-600")
      if (remaining <= 3 && remaining > 0) {
        badge.classList.add("text-amber-600")
      } else if (remaining <= 0) {
        badge.classList.add("text-red-600")
      } else {
        badge.classList.add("text-gray-500")
      }
    })

    // Show upgrade prompt if limit reached
    if (remaining <= 0) {
      this.showUpgradePrompt()
    }
  }

  showUpgradePrompt() {
    // Hide chat form
    if (this.hasChatFormTarget) {
      this.chatFormTarget.classList.add("hidden")
    }
    // Hide remaining badge
    this.remainingBadgeTargets.forEach(badge => {
      badge.classList.add("hidden")
    })
    // Show upgrade prompt
    if (this.hasUpgradePromptTarget) {
      this.upgradePromptTarget.classList.remove("hidden")
    }
    // Convert feedback buttons to upgrade links
    this.feedbackButtonTargets.forEach(btn => {
      const link = document.createElement('a')
      link.href = this.upgradeUrlValue || '/subscriptions'
      link.className = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition'
      link.innerHTML = '<i class="w-4 h-4" data-lucide="lock"></i> Upgrade for More'
      btn.replaceWith(link)
    })
    // Re-render lucide icons
    createIcons({ icons })
  }

  loadInitialTodos() {
    this.todosContainerTargets.forEach(container => {
      const beatId = container.dataset.beatId
      const initialTodos = container.dataset.initialTodos

      if (initialTodos) {
        try {
          const todos = JSON.parse(initialTodos)
          if (Array.isArray(todos) && todos.length > 0) {
            this.beatTodos[beatId] = todos
            this.renderTodosForBeat(beatId)
          }
        } catch (e) {
          console.warn("Failed to parse initial todos:", e)
        }
      }
    })
  }

  loadInitialHistory() {
    this.todosContainerTargets.forEach(container => {
      const beatId = container.dataset.beatId
      const initialHistory = container.dataset.initialHistory

      if (initialHistory) {
        try {
          const history = JSON.parse(initialHistory)
          if (Array.isArray(history) && history.length > 0) {
            this.conversationHistories[beatId] = history
          }
        } catch (e) {
          console.warn("Failed to parse initial history:", e)
        }
      }
    })
  }

  selectFirstBeat() {
    const firstBeatSection = this.beatSectionTargets[0]
    if (!firstBeatSection) return

    const beatId = firstBeatSection.dataset.beatId
    const beatName = firstBeatSection.dataset.beatName || "Unknown beat"

    this.currentBeatId = beatId

    if (this.hasCurrentBeatNameTarget) {
      this.currentBeatNameTarget.textContent = beatName
    }

    this.enableControls()
    this.loadMessagesForBeat(beatId)
  }

  trackBeat(event) {
    const textarea = event.target
    const beatId = textarea.dataset.beatId

    if (beatId && beatId !== this.currentBeatId) {
      // Find the beat section to get the beat name
      const beatSection = this.beatSectionTargets.find(s => s.dataset.beatId === beatId)
      const beatName = beatSection?.dataset.beatName || "Unknown beat"

      this.currentBeatId = beatId

      // Update the current beat display
      if (this.hasCurrentBeatNameTarget) {
        this.currentBeatNameTarget.textContent = beatName
      }

      // Enable the controls
      this.enableControls()

      // Load conversation history for this beat
      this.loadMessagesForBeat(beatId)
    }
  }

  enableControls() {
    if (this.hasFeedbackButtonTarget) {
      this.feedbackButtonTarget.disabled = false
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false
    }
  }

  disableControls() {
    if (this.hasFeedbackButtonTarget) {
      this.feedbackButtonTarget.disabled = true
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
  }

  loadMessagesForBeat(beatId) {
    const container = this.messagesContainerTarget
    if (!container) return

    // Clear current messages
    container.innerHTML = ""

    // Load history for this beat if it exists
    const history = this.conversationHistories[beatId] || []

    if (history.length === 0) {
      // Show placeholder
      container.innerHTML = `
        <div class="text-center text-gray-400 text-sm py-8">
          <p>Click "Get Feedback" to receive feedback on this beat.</p>
        </div>
      `
    } else {
      // Replay the conversation
      history.forEach(msg => {
        this.addMessageToContainer(msg.content, msg.role, container)
      })
    }
  }

  requestFeedbackForBeatButton(event) {
    const beatId = event.currentTarget.dataset.beatId
    if (!beatId || this.isLoading) return

    // Set current beat and update sidebar
    const beatSection = this.beatSectionTargets.find(s => s.dataset.beatId === beatId)
    const beatName = beatSection?.dataset.beatName || "Unknown beat"

    this.currentBeatId = beatId

    if (this.hasCurrentBeatNameTarget) {
      this.currentBeatNameTarget.textContent = beatName
    }

    this.enableControls()
    this.loadMessagesForBeat(beatId)

    // Now request feedback
    this.requestFeedbackInternal(beatId)
  }

  requestFeedback(event) {
    if (this.isLoading || !this.currentBeatId) return

    const beatId = this.currentBeatId
    this.requestFeedbackInternal(beatId)
  }

  requestFeedbackInternal(beatId) {
    if (this.isLoading) return
    const content = this.getContentForBeat(beatId)

    this.setLoading(true)
    this.addMessage("Ready for feedback", "user")

    const formData = new FormData()
    formData.append("content", content)
    formData.append("beat_id", beatId)
    formData.append("sub_beat_id", "")
    formData.append("history", JSON.stringify(this.conversationHistories[beatId] || []))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.feedbackUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then(response => response.json())
      .then(data => {
        if (data.limit_reached) {
          this.addMessage(data.error, "error")
          this.updateRemainingCount(0)
        } else if (data.error) {
          this.addMessage(data.error, "error")
        } else {
          this.addMessage(data.message, "assistant")
          if (!this.conversationHistories[beatId]) {
            this.conversationHistories[beatId] = []
          }
          this.conversationHistories[beatId].push(
            { role: "user", content: `[Beat content for feedback]:\n${content}` },
            { role: "assistant", content: data.message }
          )

          // Handle todos from feedback
          if (data.todos && data.todos.length > 0) {
            this.setTodosForBeat(beatId, data.todos)
          }

          // Auto-check completed todos with confetti
          if (data.completed_indices && data.completed_indices.length > 0) {
            this.autoCompleteTodos(beatId, data.completed_indices)
          }

          // Update remaining count
          if (data.remaining !== undefined) {
            this.updateRemainingCount(data.remaining)
          }

          // Persist conversation history
          this.persistHistory(beatId)
        }
      })
      .catch(error => {
        console.error("Feedback error:", error)
        this.addMessage("Something went wrong. Please try again.", "error")
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  sendMessage(event) {
    event?.preventDefault()

    if (this.isLoading || !this.currentBeatId) return

    const beatId = this.currentBeatId
    const input = this.inputTarget
    const message = input.value.trim()

    if (!message) return

    input.value = ""
    this.addMessage(message, "user")
    this.setLoading(true)

    const history = this.conversationHistories[beatId] || []

    const formData = new FormData()
    formData.append("message", message)
    formData.append("beat_id", beatId)
    formData.append("sub_beat_id", "")
    formData.append("history", JSON.stringify(history))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.chatUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then(response => response.json())
      .then(data => {
        if (data.limit_reached) {
          this.addMessage(data.error, "error")
          this.updateRemainingCount(0)
        } else if (data.error) {
          this.addMessage(data.error, "error")
        } else {
          this.addMessage(data.message, "assistant")
          if (!this.conversationHistories[beatId]) {
            this.conversationHistories[beatId] = []
          }
          this.conversationHistories[beatId].push(
            { role: "user", content: message },
            { role: "assistant", content: data.message }
          )

          // Update remaining count
          if (data.remaining !== undefined) {
            this.updateRemainingCount(data.remaining)
          }

          // Persist conversation history
          this.persistHistory(beatId)
        }
      })
      .catch(error => {
        console.error("Chat error:", error)
        this.addMessage("Something went wrong. Please try again.", "error")
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage({ preventDefault: () => {} })
    }
  }

  getContentForBeat(beatId) {
    const textarea = this.noteInputTargets.find(t => t.dataset.beatId === beatId)
    return textarea?.value || ""
  }

  addMessage(content, role) {
    const container = this.messagesContainerTarget
    if (!container) return

    // Remove placeholder if present
    const placeholder = container.querySelector('.text-center.py-8')
    if (placeholder) placeholder.remove()

    this.addMessageToContainer(content, role, container)

    // Speak assistant messages if voice is enabled
    if (role === "assistant" && this.voiceEnabled) {
      this.speak(content)
    }
  }

  addMessageToContainer(content, role, container) {
    const messageDiv = document.createElement("div")
    messageDiv.className = this.getMessageClasses(role)

    const contentP = document.createElement("p")
    contentP.className = "text-sm whitespace-pre-wrap"
    contentP.textContent = content

    messageDiv.appendChild(contentP)
    container.appendChild(messageDiv)

    // Scroll to bottom
    container.scrollTop = container.scrollHeight
  }

  getMessageClasses(role) {
    const baseClasses = "rounded-lg px-3 py-2"

    switch (role) {
      case "user":
        return `${baseClasses} bg-primary-100 text-primary-900 ml-8`
      case "assistant":
        return `${baseClasses} bg-white text-gray-800 border border-gray-200 mr-8`
      case "error":
        return `${baseClasses} bg-red-100 text-red-700`
      default:
        return baseClasses
    }
  }

  setLoading(loading) {
    this.isLoading = loading

    if (this.hasFeedbackButtonTarget) {
      this.feedbackButtonTarget.disabled = loading
      this.feedbackButtonTarget.classList.toggle("opacity-50", loading)
    }

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = loading
    }

    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading
    }

    if (loading) {
      this.showTypingIndicator()
    } else {
      this.hideTypingIndicator()
    }
  }

  showTypingIndicator() {
    const container = this.messagesContainerTarget
    if (!container) return

    const indicator = document.createElement("div")
    indicator.id = "typing-indicator"
    indicator.className = "flex items-center gap-1 text-gray-400 text-sm px-3 py-2"
    indicator.innerHTML = `
      <span class="animate-pulse">Thinking</span>
      <span class="animate-bounce" style="animation-delay: 0ms">.</span>
      <span class="animate-bounce" style="animation-delay: 150ms">.</span>
      <span class="animate-bounce" style="animation-delay: 300ms">.</span>
    `

    container.appendChild(indicator)
    container.scrollTop = container.scrollHeight
  }

  hideTypingIndicator() {
    const indicator = document.getElementById("typing-indicator")
    indicator?.remove()
  }

  toggleVoice() {
    this.voiceEnabled = !this.voiceEnabled

    // Update button title
    if (this.hasVoiceToggleTarget) {
      this.voiceToggleTarget.title = this.voiceEnabled ? "Toggle voice (on)" : "Toggle voice (off)"
    }

    // Toggle icons - show volume-2 when on, volume-x when off
    if (this.hasVoiceOnIconTarget && this.hasVoiceOffIconTarget) {
      this.voiceOnIconTarget.classList.toggle("hidden", !this.voiceEnabled)
      this.voiceOffIconTarget.classList.toggle("hidden", this.voiceEnabled)
    }

    // Stop any current audio when toggling off
    if (!this.voiceEnabled) {
      this.stopSpeaking()
    }
  }

  async speak(text) {
    if (!this.voiceEnabled) return
    if (!this.speakUrlValue) {
      console.warn("Speak URL not configured")
      return
    }

    // Stop any current audio
    this.stopSpeaking()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.speakUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: new URLSearchParams({ text: text })
      })

      if (!response.ok) {
        console.error("TTS request failed:", response.status)
        return
      }

      const audioBlob = await response.blob()
      const audioUrl = URL.createObjectURL(audioBlob)

      this.currentAudio = new Audio(audioUrl)
      this.currentAudio.onended = () => {
        URL.revokeObjectURL(audioUrl)
        this.currentAudio = null
      }
      this.currentAudio.onerror = (e) => {
        console.error("Audio playback error:", e)
        URL.revokeObjectURL(audioUrl)
        this.currentAudio = null
      }

      await this.currentAudio.play()
    } catch (error) {
      console.error("TTS error:", error)
    }
  }

  stopSpeaking() {
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio.currentTime = 0
      this.currentAudio = null
    }
  }

  // Todo management methods
  setTodosForBeat(beatId, todoTexts) {
    // Convert string array to objects with completed state
    this.beatTodos[beatId] = todoTexts.map(text => ({
      text: text,
      completed: false
    }))
    this.renderTodosForBeat(beatId)
  }

  renderTodosForBeat(beatId) {
    const container = this.todosContainerTargets.find(c => c.dataset.beatId === beatId)
    if (!container) return

    const todos = this.beatTodos[beatId] || []

    if (todos.length === 0) {
      container.classList.add("hidden")
      container.innerHTML = ""
      return
    }

    container.classList.remove("hidden")
    container.innerHTML = ""

    todos.forEach((todo, index) => {
      const isCompleted = todo.completed === true || todo.completed === "true"

      const todoEl = document.createElement("label")
      todoEl.className = `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all ${
        isCompleted
          ? "bg-gray-100 text-gray-400 line-through"
          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
      }`

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.checked = isCompleted
      checkbox.className = "w-3.5 h-3.5 rounded accent-amber-600"
      checkbox.addEventListener("change", () => this.toggleTodo(beatId, index))

      const textSpan = document.createElement("span")
      textSpan.textContent = todo.text

      todoEl.appendChild(checkbox)
      todoEl.appendChild(textSpan)
      container.appendChild(todoEl)
    })
  }

  toggleTodo(beatId, index) {
    const key = String(beatId)
    if (!this.beatTodos[key] || !this.beatTodos[key][index]) return

    const current = this.beatTodos[key][index].completed === true || this.beatTodos[key][index].completed === "true"
    this.beatTodos[key][index].completed = !current
    this.renderTodosForBeat(key)
    this.persistTodos(key)
  }

  clearTodosForBeat(beatId) {
    delete this.beatTodos[beatId]
    this.renderTodosForBeat(beatId)
    this.persistTodos(beatId)
  }

  async persistTodos(beatId) {
    if (!this.updateTodosUrlValue) return

    const key = String(beatId)
    const todos = this.beatTodos[key] || []
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.updateTodosUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ beat_id: key, note: { coach_todos: todos } })
      })
    } catch (error) {
      console.error("Failed to persist todos:", error)
    }
  }

  async persistHistory(beatId) {
    if (!this.updateHistoryUrlValue) return

    const key = String(beatId)
    const history = this.conversationHistories[key] || []
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.updateHistoryUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ beat_id: key, history: JSON.stringify(history) })
      })
    } catch (error) {
      console.error("Failed to persist history:", error)
    }
  }

  autoCompleteTodos(beatId, completedIndices) {
    const key = String(beatId)
    if (!this.beatTodos[key]) return

    let anyCompleted = false

    completedIndices.forEach(index => {
      if (this.beatTodos[key][index] && !this.beatTodos[key][index].completed) {
        this.beatTodos[key][index].completed = true
        anyCompleted = true
      }
    })

    if (anyCompleted) {
      this.renderTodosForBeat(key)
      this.persistTodos(key)
      this.fireSmallConfetti()
    }
  }

  fireSmallConfetti() {
    // A small, celebratory confetti burst for completing todos
    const colors = ['#f97316', '#fb923c', '#fbbf24', '#10b981']

    confetti({
      particleCount: 30,
      spread: 60,
      origin: { y: 0.7 },
      colors: colors,
      ticks: 100,
      gravity: 1.2,
      scalar: 0.8
    })
  }
}
